import type {
  Value,
  Tolerance,
  ToleranceType,
  PrecisionClass,
  RationalValue,
  SimplifiedValue,
  Units,
} from '@frontend/api';
import { TOLERANCE_TYPES, PRECISION_CLASSES } from '@frontend/api';

import { parsePrecisionValue, PrecisionValue } from './validation';

/**
 * Converts precision (number of decimal places) to PrecisionClass enum
 * @param precision - Number of decimal places (0 for integers)
 * @returns PrecisionClass enum value
 */
const precisionToPrecisionClass = (precision: number): PrecisionClass => {
  switch (precision) {
    case 0:
      return PRECISION_CLASSES.INTEGER;
    case 1:
      return PRECISION_CLASSES.ONE_DECIMAL_PLACES;
    case 2:
      return PRECISION_CLASSES.TWO_DECIMAL_PLACES;
    case 3:
      return PRECISION_CLASSES.THREE_DECIMAL_PLACES;
    case 4:
      return PRECISION_CLASSES.FOUR_DECIMAL_PLACES;
    default:
      return PRECISION_CLASSES.FIVE_DECIMAL_PLACES;
  }
};

/**
 * Builds a RationalValue from a PrecisionValue
 * @param precisionValue - The parsed precision value
 * @returns RationalValue object
 */
const buildRationalValue = (
  precisionValue: PrecisionValue,
  units?: Units | null,
): RationalValue => {
  return {
    numericValue: precisionValue.numericValue,
    units: units ?? null,
    precisionClass: precisionToPrecisionClass(precisionValue.precision),
  };
};

/**
 * Builds a SimplifiedValue from a PrecisionValue
 * @param precisionValue - The parsed precision value
 * @returns SimplifiedValue object
 */
const buildSimplifiedValue = (
  precisionValue: PrecisionValue,
  units?: Units | null,
): SimplifiedValue => {
  return {
    value: precisionValue.numericValue,
    preferredUnits: units ?? null,
    precisionClass: precisionToPrecisionClass(precisionValue.precision),
  };
};

/**
 * Builds a Value object from a string input, preserving precision
 * @param stringValue - The string representation of the value
 * @returns Value object for API consumption, or undefined if invalid
 */
export const buildPrecisionValueObject = (
  stringValue: string,
  units?: Units | null,
): Value | undefined => {
  const parsed = parsePrecisionValue(stringValue);
  if (!parsed) {
    return undefined;
  }

  const rationalValue = buildRationalValue(parsed, units);
  const simplifiedValue = buildSimplifiedValue(parsed, units);

  return {
    value: {
      mixedUnitValueComponents: [rationalValue],
    },
    simplifiedValue,
  };
};

/**
 * Configuration for building tolerance objects
 */
type ToleranceConfig = {
  toleranceType: ToleranceType;
  baseValue?: number; // Required for LIMIT type to calculate deltas
  symmetricValue?: PrecisionValue;
  upperValue?: PrecisionValue;
  lowerValue?: PrecisionValue;
};

/**
 * Builds a Value object from a PrecisionValue
 * @param precisionValue - The parsed precision value
 * @returns Value object
 */
const buildValueFromPrecisionValue = (
  precisionValue: PrecisionValue,
  units?: Units | null,
): Value => {
  const rationalValue = buildRationalValue(precisionValue, units);
  const simplifiedValue = buildSimplifiedValue(precisionValue, units);

  return {
    value: {
      mixedUnitValueComponents: [rationalValue],
    },
    simplifiedValue,
  };
};

/**
 * Builds a Tolerance object from form values
 * @param config - Configuration object with tolerance values
 * @returns Tolerance object for API consumption, or undefined if invalid
 */
export const buildToleranceObject = (
  config: ToleranceConfig,
  units?: Units | null,
): Tolerance | undefined => {
  const { toleranceType, baseValue, symmetricValue, upperValue, lowerValue } =
    config;

  switch (toleranceType) {
    case TOLERANCE_TYPES.SYMMETRIC:
      if (symmetricValue !== undefined) {
        const value = buildValueFromPrecisionValue(symmetricValue, units);
        return {
          tolType: TOLERANCE_TYPES.SYMMETRIC,
          upper: value,
          lower: value,
        };
      }
      break;

    case TOLERANCE_TYPES.ASYMMETRIC:
      if (upperValue !== undefined && lowerValue !== undefined) {
        return {
          tolType: TOLERANCE_TYPES.ASYMMETRIC,
          upper: buildValueFromPrecisionValue(upperValue, units),
          lower: buildValueFromPrecisionValue(lowerValue, units),
        };
      }
      break;

    case TOLERANCE_TYPES.LIMIT:
      if (
        upperValue !== undefined &&
        lowerValue !== undefined &&
        baseValue !== undefined
      ) {
        const upperDelta = Math.abs(upperValue.numericValue - baseValue);
        const lowerDelta = Math.abs(lowerValue.numericValue - baseValue);

        const upperDeltaPrecisionValue: PrecisionValue = {
          stringValue: upperDelta.toString(),
          numericValue: upperDelta,
          precision: upperValue.precision,
        };
        const lowerDeltaPrecisionValue: PrecisionValue = {
          stringValue: lowerDelta.toString(),
          numericValue: lowerDelta,
          precision: lowerValue.precision,
        };

        return {
          tolType: TOLERANCE_TYPES.LIMIT,
          upper: buildValueFromPrecisionValue(upperDeltaPrecisionValue, units),
          lower: buildValueFromPrecisionValue(lowerDeltaPrecisionValue, units),
        };
      }
      break;
  }

  return undefined;
};

/**
 * Builds a precision-aware measurement object with value and optional tolerance
 * @param stringValue - The string representation of the base measurement value
 * @param toleranceConfig - Optional tolerance configuration
 * @returns Object with value and optional tolerance, or undefined if invalid
 */
export const buildPrecisionMeasurementObject = (
  stringValue: string,
  toleranceConfig?: ToleranceConfig,
  units?: Units | null,
): { value: Value; tol?: Tolerance } | undefined => {
  const valueObject = buildPrecisionValueObject(stringValue, units);
  if (!valueObject) {
    return undefined;
  }

  const result: { value: Value; tol?: Tolerance } = {
    value: valueObject,
  };

  if (toleranceConfig) {
    const tolerance = buildToleranceObject(toleranceConfig, units);
    if (tolerance) {
      result.tol = tolerance;
    }
  }

  return result;
};

/**
 * Helper type for string-based tolerance form values
 */
export type StringToleranceFormValues = {
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceSymmetric?: string;
  toleranceUpper?: string;
  toleranceLower?: string;
};

/**
 * Converts string-based tolerance form values to ToleranceConfig
 * @param formValues - String-based form values for tolerance
 * @param baseValue - Base value for LIMIT type calculations
 * @returns ToleranceConfig object or undefined if tolerance is disabled
 */
export const stringToleranceFormValuesToConfig = (
  formValues: StringToleranceFormValues,
  baseValue?: number,
): ToleranceConfig | undefined => {
  if (!formValues.hasTolerance) {
    return undefined;
  }

  // Parse string values to numbers
  const symmetricValue = formValues.toleranceSymmetric
    ? parsePrecisionValue(formValues.toleranceSymmetric)
    : undefined;
  const upperValue = formValues.toleranceUpper
    ? parsePrecisionValue(formValues.toleranceUpper)
    : undefined;
  const lowerValue = formValues.toleranceLower
    ? parsePrecisionValue(formValues.toleranceLower)
    : undefined;

  return {
    toleranceType: formValues.toleranceType,
    baseValue,
    symmetricValue: symmetricValue ?? undefined,
    upperValue: upperValue ?? undefined,
    lowerValue: lowerValue ?? undefined,
  };
};

/**
 * Helper type for multiplier form values
 */
export type MultiplierFormValues = {
  hasMult: boolean;
  mult?: number;
};

/**
 * Extracts multiplier value from form values
 * @param formValues - Form values for multiplier
 * @returns Multiplier value or undefined if disabled/invalid
 */
export const extractMultiplier = (
  formValues: MultiplierFormValues,
): number | undefined => {
  return formValues.hasMult && formValues.mult ? formValues.mult : undefined;
};
